import { RatingWithChange } from "../../components/RatingWithChange.tsx";
import type { ReactNode } from "react";
import i18n from "../../util/i18n.ts";

const RatingsOverview = ({
	ratings,
	season,
}: {
	ratings: any[];
	season?: number;
}) => {
	let currentSeason;
	if (season === undefined) {
		// Use latest season
		currentSeason = ratings.at(-1);
	} else {
		currentSeason =
			ratings.findLast((row) => row.season === season) ?? ratings.at(-1);
	}

	const lastSeason =
		ratings.findLast((row) => row.season === currentSeason.season - 1) ??
		currentSeason;

	const sectionNames: Record<string, string> = {
		Physical: i18n.t("ratingsOverview.physical", { ns: "ui", defaultValue: "Physical" }),
		Shooting: i18n.t("ratingsOverview.shooting", { ns: "ui", defaultValue: "Shooting" }),
		Skill: i18n.t("ratingsOverview.skill", { ns: "ui", defaultValue: "Skill" }),
	};

	const columns: Record<
		string,
		{
			label: ReactNode;
			rating: string;
		}[]
	>[] = [
		{
			Physical: [
				{
					label: i18n.t("ratingsOverview.height", { ns: "ui", defaultValue: "Height" }),
					rating: "hgt",
				},
				{
					label: i18n.t("ratingsOverview.strength", { ns: "ui", defaultValue: "Strength" }),
					rating: "stre",
				},
				{
					label: i18n.t("ratingsOverview.speed", { ns: "ui", defaultValue: "Speed" }),
					rating: "spd",
				},
				{
					label: i18n.t("ratingsOverview.jumping", { ns: "ui", defaultValue: "Jumping" }),
					rating: "jmp",
				},
				{
					label: i18n.t("ratingsOverview.endurance", { ns: "ui", defaultValue: "Endurance" }),
					rating: "endu",
				},
			],
		},
		{
			Shooting: [
				{
					label: i18n.t("ratingsOverview.inside", { ns: "ui", defaultValue: "Inside" }),
					rating: "ins",
				},
				{
					label: (
						<>
							<span className="d-md-none">{i18n.t("ratingsOverview.layups_short", { ns: "ui", defaultValue: "Layups" })}</span>
							<span className="d-none d-md-inline">{i18n.t("ratingsOverview.dunksLayups", { ns: "ui", defaultValue: "Dunks/Layups" })}</span>
						</>
					),
					rating: "dnk",
				},
				{
					label: (
						<>
							<span className="d-md-none">{i18n.t("ratingsOverview.freeThrows_short", { ns: "ui", defaultValue: "FT" })}</span>
							<span className="d-none d-md-inline">{i18n.t("ratingsOverview.freeThrows", { ns: "ui", defaultValue: "Free Throws" })}</span>
						</>
					),
					rating: "ft",
				},
				{
					label: i18n.t("ratingsOverview.midRange", { ns: "ui", defaultValue: "Mid Range" }),
					rating: "fg",
				},
				{
					label: (
						<>
							<span className="d-md-none">{i18n.t("ratingsOverview.threePointers_short", { ns: "ui", defaultValue: "3 Pt" })}</span>
							<span className="d-none d-md-inline">{i18n.t("ratingsOverview.threePointers", { ns: "ui", defaultValue: "Three Pointers" })}</span>
						</>
					),
					rating: "tp",
				},
			],
		},
		{
			Skill: [
				{
					label: (
						<>
							<span className="d-md-none">{i18n.t("ratingsOverview.offIQ_short", { ns: "ui", defaultValue: "Off IQ" })}</span>
							<span className="d-none d-md-inline">{i18n.t("ratingsOverview.offensiveIQ", { ns: "ui", defaultValue: "Offensive IQ" })}</span>
						</>
					),
					rating: "oiq",
				},
				{
					label: (
						<>
							<span className="d-md-none">{i18n.t("ratingsOverview.defIQ_short", { ns: "ui", defaultValue: "Def IQ" })}</span>
							<span className="d-none d-md-inline">{i18n.t("ratingsOverview.defensiveIQ", { ns: "ui", defaultValue: "Defensive IQ" })}</span>
						</>
					),
					rating: "diq",
				},
				{
					label: i18n.t("ratingsOverview.dribbling", { ns: "ui", defaultValue: "Dribbling" }),
					rating: "drb",
				},
				{
					label: i18n.t("ratingsOverview.passing", { ns: "ui", defaultValue: "Passing" }),
					rating: "pss",
				},
				{
					label: (
						<>
							<span className="d-md-none">{i18n.t("ratingsOverview.rebounding_short", { ns: "ui", defaultValue: "Reb" })}</span>
							<span className="d-none d-md-inline">{i18n.t("ratingsOverview.rebounding", { ns: "ui", defaultValue: "Rebounding" })}</span>
						</>
					),
					rating: "reb",
				},
			],
		},
	];

	return (
		<div className="ratings-overview">
			<div className="d-flex justify-content-between">
				<h2 className="me-3">
					{i18n.t("ratingsOverview.overall", { ns: "ui", defaultValue: "Overall" })}:{" "}
					<RatingWithChange change={currentSeason.ovr - lastSeason.ovr}>
						{currentSeason.ovr}
					</RatingWithChange>
				</h2>
				<h2>
					{i18n.t("ratingsOverview.potential", { ns: "ui", defaultValue: "Potential" })}:{" "}
					<RatingWithChange change={currentSeason.pot - lastSeason.pot}>
						{currentSeason.pot}
					</RatingWithChange>
				</h2>
			</div>
			<div className="d-flex justify-content-between">
				{columns.map((column, i) => (
					<div key={i} className={i === 0 ? undefined : "ms-2 ms-sm-5"}>
						{Object.entries(column).map(([name, categories], j) => (
							<div key={name} className={j === 0 ? undefined : "mt-2"}>
								<table>
									<thead>
										<tr className="border-bottom">
											<th className="p-0" colSpan={2}>
												{sectionNames[name] ?? name}
											</th>
										</tr>
									</thead>
									<tbody>
										{categories.map(({ label, rating }, j) => (
											<tr key={j}>
												<td className="p-0">{label}:</td>
												<td className="p-0 ps-1">
													<RatingWithChange
														change={currentSeason[rating] - lastSeason[rating]}
													>
														{currentSeason[rating]}
													</RatingWithChange>
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						))}
					</div>
				))}
			</div>
		</div>
	);
};

export default RatingsOverview;
